/**
 * 처방 판정 테스트 — 판정 API 클라이언트 + 응답 미러 타입.
 *
 * 정본은 백엔드(`ymyd-prai-backend`)의
 * `src/modules/dose-review/dto/dose-review.dto.ts`와
 * `docs/api-specs/dose-review-api.md`다. 이 파일은 그 계약을 그대로 미러링한 것으로,
 * **필드명·타입·상태값을 임의로 바꾸지 않는다.** 백엔드 코드는 이 repo에서 고치지 않는다.
 *
 * 설계 결정 8(계획 `docs/ai-discussions/20260917-dose-review-처방판정-테스트패널.md`):
 * - 이 클라이언트는 실패해도 **reject하지 않는다** — `</>` 원문 뷰에 실패 응답 원문을
 *   남겨야 하기 때문이다. 대신 통일 응답 `{ data?, error?, message?, status, body, request }`를
 *   돌려준다(§7 통일 응답 + 원문 보존 필드).
 * - `status: null`은 응답 없음(네트워크·타임아웃 10초). `body`는 응답 본문 원문,
 *   `request`는 실제로 보낸 요청 스냅샷이다(`</>`가 이 둘을 보여준다).
 * - 400 `message` 배열은 반환 `message`에 `"; "`로 이어 붙이고, `body`에는 배열 원문을 남긴다.
 * - 캐시를 쓰지 않는다 — 룰 승인 뒤 같은 처방을 다시 실행하면 새 요청이 나가야 한다.
 */
import axiosInstance from "./axiosInstance";
import { log, LogLevel } from "utils/LogUtil";

// ── 요청 계약 (dose-review.dto.ts:30-118 미러) ──

/** 환자 체중·월령. 빈 값은 키를 생략한다(0은 서버가 거부·오판정). */
export interface DoseReviewPatientInput {
  weightKg?: number; // @IsPositive — 0·음수 거부
  ageMonths?: number; // @Min(0)
}

/** 검토 대상 처방 1건 — CreatePrescriptionDto와 동일 필드명. */
export interface DoseReviewPrescriptionInput {
  standardCode?: string; // 1차 해석 키
  itemCode?: string; // 해석에 쓰지 않음(참고). 요청에는 싣지 않는다(설계 결정 9)
  insuranceCode?: string; // 2차 폴백 키
  medicationName?: string; // 표시용 — 응답에 반사
  dosagePerAdministration?: number; // 1회 투약량
  dosageUnit?: string; // 판정 미사용
  timesPerDay?: number; // 1일 횟수
  daysOfAdministration?: number; // 판정 미사용
}

export interface DoseReviewRequest {
  patient?: DoseReviewPatientInput;
  patientId?: number; // 패널은 보내지 않는다(체중은 DB에 없음)
  prescriptions: DoseReviewPrescriptionInput[];
}

// ── 응답 계약 (dose-review.dto.ts:120-257 미러) ──

/** 5값 — null은 적정. 순위 critical>caution>warning>info>null. */
export type Severity = "critical" | "caution" | "warning" | "info" | null;

/** 판정 상태 문자열(dose-review-api.md:126). */
export type DoseStatusStr = "overdose" | "underdose" | "unknown" | "appropriate";

/** 체중 출처(OQ12). given=요청 체중 유효, unavailable=미상. */
export type WeightSource = "given" | "unavailable";

/**
 * 미판정 사유 어휘(dto:202-215 미러). ⚠️ extraction_pending·no_source는 배출 코드가 없는
 * 허위 2종이나 union에 남아 있다(D-10). 실제 배출 11종은 사유 문구 맵이 다룬다.
 */
export type UnevaluatedReason =
  | "product_not_registered"
  | "not_eligible"
  | "weight_required"
  | "rule_not_found"
  | "blocked"
  | "awaiting_unit"
  | "prescribe_unit_missing"
  | "no_bridge"
  | "extraction_pending"
  | "no_source"
  | "code_missing"
  | "insurance_code_not_registered"
  | "insurance_code_ambiguous";

export interface RecommendedRangeOut {
  min: number | null;
  max: number | null;
  dayCap: number | null;
  unit: string;
}

export interface MatchedSegmentOut {
  ageMinMonths: number | null;
  ageMaxMonths: number | null;
  weightMinKg: number | null;
  weightMaxKg: number | null;
  isDefault: boolean;
}

export interface MatchedSelectorOut {
  axis: string;
  value: string;
}

export interface FindingProvenanceOut {
  source: string;
  evidenceLevel: string | null;
  citation: string | null;
  stale: boolean;
  trustSignals: null; // 다약사 단계 전까지 항상 null (D-7)
}

export interface MitigatedByOut {
  ruleId: string;
  author: string | null;
  evidenceGrade: string | null;
  scope: "pharmacy";
  matchedSelector: MatchedSelectorOut[];
  matchedBy: string;
  rationale: string | null;
  from: Severity;
  to: Severity;
}

export interface DoseFinding {
  ruleId: string;
  class: string;
  type: string;
  applied: boolean;
  supersededBy: string | null;
  skippedReason: string | null;
  severity: Severity; // 완화·강등 반영 후 최종 등급
  originalSeverity: Severity; // L1 원 등급(강등 반영, 완화 전)
  status: DoseStatusStr;
  reason: string;
  recommendedRange: RecommendedRangeOut | null;
  matchedSegment: MatchedSegmentOut | null;
  matchedTo: string | null;
  matchedSelector: MatchedSelectorOut | null;
  matchedBy: string;
  scope: "central" | "pharmacy";
  pendingReview: boolean;
  pendingDowngrade: { from: Severity; to: Severity; reason: string } | null;
  provenance: FindingProvenanceOut;
  mitigatedBy: MitigatedByOut | null;
  mitigationRejected: string | null;
}

export interface DoseResolvedOut {
  productKey: string | null;
  standardCode: string | null;
  insuranceCode: string | null;
  itemCode: string | null;
  productName: string | null;
  prescribeUnit: string | null;
  resolvedBy: "standard_code" | "insurance_code" | null;
}

/** 허가사항 참고 문구(D9) — labelNotes[]. */
export interface DoseLabelNote {
  type: string; // not_established·not_recommended·patient_condition·merged_indications
  message: string;
  citation: string | null;
}

export interface DoseReviewItem {
  index: number;
  resolved: DoseResolvedOut | null;
  findings: DoseFinding[];
  labelNotes: DoseLabelNote[]; // 항상 존재(기본 [])
  unevaluatedReason: UnevaluatedReason | null;
  ineligibleReason: string | null;
  candidateCount: number | null;
  codeConflict: boolean | null;
}

export interface PrescriptionFinding {
  type: "duplicate_ingredient";
  severity: Severity; // 현재 critical 고정(강등 없음)
  ingredient: string;
  itemIndexes: number[];
  message: string;
  ruleId: string | null; // 성분 중복은 항상 null
}

export interface DoseReviewData {
  patient: { weightKg: number | null; weightSource: WeightSource; ageMonths: number | null };
  severityMax: Severity;
  summary: {
    total: number;
    appropriate: number;
    caution: number;
    critical: number;
    warning: number;
    info: number;
    unevaluated: number;
    mitigated: number;
    /**
     * 미달(참고) 수 — status underdose + severity info인 채택 판정 수(백엔드 Phase 2 신규 필드).
     * 옛 응답엔 없어 optional. 없으면 화면은 `warning`으로 폴백한다(D-3·D-5).
     */
    underdose?: number;
  };
  items: DoseReviewItem[];
  prescriptionFindings: PrescriptionFinding[];
  coverage: { resolved: number; unresolved: number; ruleMissing: number };
  unknownRuleClasses: string[];
}

// ── 클라이언트 반환 형태(설계 결정 8) ──

export interface DoseReviewResult {
  /** 성공 시 응답의 `data`. */
  data?: DoseReviewData;
  /** 실패면 true. reject하지 않고 이 플래그로 알린다. */
  error?: boolean;
  /** 실패 메시지(400 배열은 "; "로 이음). */
  message?: string;
  /** HTTP 상태. null이면 응답 없음(네트워크·타임아웃). */
  status: number | null;
  /** 응답 본문 원문(`</>`가 보여준다). 응답 없음이면 null. */
  body: unknown;
  /** 실제로 보낸 요청 스냅샷(`</>`가 보여준다). */
  request: DoseReviewRequest;
}

/** 응답 본문에서 표시용 메시지를 뽑는다. 배열(400)은 "; "로 잇는다. */
const extractMessage = (body: unknown): string | undefined => {
  if (typeof body === "string") return body || undefined;
  if (!body || typeof body !== "object") return undefined;
  const m = (body as { message?: unknown }).message;
  if (Array.isArray(m)) return m.filter((x) => x != null).map(String).join("; ") || undefined;
  if (typeof m === "string") return m || undefined;
  return undefined;
};

const ENDPOINT = "/api/v1/dose-review";

/**
 * 판정 API 호출. 실패해도 reject하지 않는다(설계 결정 8). 캐시를 쓰지 않는다.
 * 요청 body는 호출부가 만든 스냅샷을 그대로 보낸다(변환은 순수 함수 몫).
 */
export const requestDoseReview = async (
  request: DoseReviewRequest
): Promise<DoseReviewResult> => {
  try {
    // 출처 헤더(D-10) — 백엔드 mapper가 PRAI_ADMIN을 출처 "테스트 패널"로 매핑한다.
    // 이 헤더로 판정 기록 목록의 출처 필터가 테스트 패널 호출을 가른다.
    const response = await axiosInstance.post(ENDPOINT, request, {
      headers: { "X-Ymyd-Client": "PRAI_ADMIN" },
    });
    const body = response?.data;
    const status = typeof response?.status === "number" ? response.status : 200;

    // (c) 2xx인데 봉투 error ≠ 0 — 패널은 patientId를 안 보내 현재 도달 불가, 방어용.
    if (body && typeof body === "object" && "error" in body && (body as { error?: unknown }).error) {
      return { error: true, message: extractMessage(body), status, body, request };
    }

    const data =
      body && typeof body === "object" && "data" in body
        ? ((body as { data?: unknown }).data as DoseReviewData)
        : (body as DoseReviewData);
    return { data, status, body, request };
  } catch (err) {
    const resp = (err as { response?: { status?: number; data?: unknown } })?.response;
    if (resp) {
      // (a) 4xx·5xx — HTTP 상태 + message(400은 배열). 403 등 카드가 상태별 문구를 정한다.
      const status = typeof resp.status === "number" ? resp.status : null;
      return {
        error: true,
        message: extractMessage(resp.data) ?? `요청이 실패했습니다 (HTTP ${status ?? "?"}).`,
        status,
        body: resp.data ?? null,
        request,
      };
    }
    // (b) 응답 없음 — 타임아웃 10초·네트워크 오류.
    log(LogLevel.ERROR, "판정 요청 응답 없음:", err);
    return {
      error: true,
      message: "응답 없음 — 네트워크 또는 시간 초과(10초)",
      status: null,
      body: null,
      request,
    };
  }
};
